import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Star, Trophy, CheckCircle, XCircle, X, Loader2, Sparkles } from 'lucide-react';

// Post-game signal capture modal. Surfaces on player dashboard when there's a
// completed game without a submission yet. Powers Phase 2C matchmaking quality.
export default function ArenaxPostGameModal({ game, onClose, onSubmitted }) {
  const [attended, setAttended] = useState(null);
  const [gameRating, setGameRating] = useState(0);
  const [venueRating, setVenueRating] = useState(0);
  const [competitiveness, setCompetitiveness] = useState(0);
  const [mvp, setMvp] = useState(null);
  const [wouldPlayAgain, setWouldPlayAgain] = useState(null);
  const [wpaPerPlayer, setWpaPerPlayer] = useState({}); // { player_id: bool }
  const [busy, setBusy] = useState(false);

  const participants = (game.participants_snapshot || []).filter(p => p.player_id !== game.my_id);

  const submit = async () => {
    if (attended === null) { toast.error('Did you attend?'); return; }
    setBusy(true);
    try {
      await api.post(`/arenax/games/${game.id}/post-game`, {
        attended,
        game_rating: gameRating || null,
        venue_rating: venueRating || null,
        competitiveness_rating: competitiveness || null,
        mvp_voted_for: mvp,
        would_play_again: wouldPlayAgain,
        wpa_per_player: Object.keys(wpaPerPlayer).length > 0 ? wpaPerPlayer : null,
      });
      toast.success('Thanks! Your signal makes matchmaking smarter.');
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 flex items-center justify-center p-4" onClick={onClose} data-testid="arenax-post-game-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-2xl border border-cyan-500/20 max-w-md w-full max-h-[92vh] overflow-y-auto"
        style={{ background: 'linear-gradient(160deg, #0a1424, #02050b)' }}>
        <div className="flex items-center justify-between p-5 border-b border-slate-800 sticky top-0 z-10"
          style={{ background: 'var(--ax-card-bg-soft)', backdropFilter: 'blur(12px)' }}>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-300 font-bold mb-1">POST-GAME · 30 SECONDS</div>
            <h3 className="text-base font-bold text-slate-100">{game.title}</h3>
            <div className="text-xs text-slate-500">{game.date} · {game.start_time}</div>
          </div>
          <button onClick={onClose} data-testid="arenax-post-game-close"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Attendance */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block">Did you attend?</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setAttended(true)} data-testid="arenax-post-game-attended-yes"
                className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                  attended === true ? 'bg-emerald-500/20 border-emerald-500 text-emerald-200' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-emerald-500/40'
                }`}>
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-bold">Yes, I played</span>
              </button>
              <button onClick={() => setAttended(false)} data-testid="arenax-post-game-attended-no"
                className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                  attended === false ? 'bg-rose-500/20 border-rose-500 text-rose-200' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-rose-500/40'
                }`}>
                <XCircle className="h-4 w-4" />
                <span className="text-sm font-bold">Couldn't make it</span>
              </button>
            </div>
          </div>

          {attended === true && (
            <>
              <RatingRow label="How was the game?" value={gameRating} setValue={setGameRating} testidPrefix="arenax-post-game-rating" />
              <RatingRow label="Venue quality" value={venueRating} setValue={setVenueRating} testidPrefix="arenax-post-game-venue" />
              <RatingRow label="Competitiveness level" value={competitiveness} setValue={setCompetitiveness} testidPrefix="arenax-post-game-comp" />

              {participants.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block flex items-center gap-1.5">
                    <Trophy className="h-3.5 w-3.5 text-amber-300" /> MVP vote (optional)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {participants.slice(0, 6).map(p => (
                      <button key={p.player_id} onClick={() => setMvp(mvp === p.player_id ? null : p.player_id)}
                        data-testid={`arenax-post-game-mvp-${p.player_id}`}
                        className={`p-2 rounded-lg border text-xs font-bold transition-all ${
                          mvp === p.player_id ? 'bg-amber-500/20 border-amber-500 text-amber-200' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-amber-500/40'
                        }`}>
                        {p.player_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block">Would you play with this group again?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setWouldPlayAgain(true)} data-testid="arenax-post-game-replay-yes"
                    className={`p-2.5 rounded-lg border text-sm font-bold transition-all ${
                      wouldPlayAgain === true ? 'bg-cyan-500/20 border-cyan-500 text-cyan-200' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-cyan-500/40'
                    }`}>Yes 👍</button>
                  <button onClick={() => setWouldPlayAgain(false)} data-testid="arenax-post-game-replay-no"
                    className={`p-2.5 rounded-lg border text-sm font-bold transition-all ${
                      wouldPlayAgain === false ? 'bg-slate-700 border-slate-500 text-slate-300' : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                    }`}>Pass 👎</button>
                </div>
              </div>

              {/* Phase 2D — Per-player "would play again" — the highest-signal chemistry input.
                  Optional + skippable. Pure tap UX — no required answer. */}
              {participants.length > 0 && (
                <div data-testid="arenax-post-game-wpa-per-player">
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-pink-300" /> Anyone you'd specifically want on your team again?
                  </label>
                  <p className="text-[10px] text-slate-500 mb-2">Tap a name to mark them. (Optional — this is private.)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {participants.slice(0, 8).map(p => {
                      const state = wpaPerPlayer[p.player_id]; // true | undefined
                      return (
                        <button key={p.player_id}
                          data-testid={`arenax-post-game-wpa-${p.player_id}`}
                          onClick={() => setWpaPerPlayer(prev => {
                            const next = { ...prev };
                            if (state === true) delete next[p.player_id];
                            else next[p.player_id] = true;
                            return next;
                          })}
                          className={`p-2 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                            state === true ? 'bg-pink-500/20 border-pink-500 text-pink-100' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-pink-500/40'
                          }`}>
                          {state === true ? '✓ ' : ''}{p.player_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-5 border-t border-slate-800 sticky bottom-0" style={{ background: 'var(--ax-card-bg-soft)', backdropFilter: 'blur(12px)' }}>
          <button onClick={submit} disabled={busy || attended === null} data-testid="arenax-post-game-submit"
            className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'var(--ax-primary-gradient)' }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" />Submit & Power Matchmaking</>}
          </button>
          <div className="text-[10px] text-slate-600 text-center mt-2">Your signal helps the AI recommend better games next time.</div>
        </div>
      </div>
    </div>
  );
}

function RatingRow({ label, value, setValue, testidPrefix }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block">{label}</label>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setValue(value === n ? 0 : n)}
            data-testid={`${testidPrefix}-${n}`}
            className="p-2 transition-all hover:scale-110">
            <Star className={`h-7 w-7 ${value >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
          </button>
        ))}
      </div>
    </div>
  );
}
